import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';

const ContactForm = ({ variant = 'default' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    message: ''
  });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!privacyAccepted) {
      toast.error('Devi accettare l\'Informativa Privacy per procedere.');
      return;
    }

    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      const response = await fetch(`${API_URL}/api/forms/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          privacy_accepted: privacyAccepted,
          form_type: variant === 'quote' ? 'quote' : 'contact'
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSubmitted(true);
        toast.success(data.message || 'Richiesta inviata con successo! Ti contatteremo presto.');
        
        // Reset after 3 seconds
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({ name: '', email: '', phone: '', city: '', message: '' });
          setPrivacyAccepted(false);
        }, 3000);
      } else {
        toast.error(data.detail || 'Errore durante l\'invio. Riprova più tardi.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Errore di connessione. Verifica la tua connessione e riprova.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-semibold text-slate-800 mb-2">Richiesta Inviata!</h3>
        <p className="text-slate-600">Ti contatteremo al più presto per il tuo preventivo.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Nome e Cognome */}
      <div className="space-y-2">
        <Label htmlFor="name" className="text-slate-700">Nome e Cognome *</Label>
        <Input
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Mario Rossi"
          required
          className="h-12 bg-white border-slate-200 focus:border-amber-500 focus:ring-amber-500"
        />
      </div>

      {/* Email e Telefono */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-slate-700">Email *</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="mario.rossi@email.com"
            required
            className="h-12 bg-white border-slate-200 focus:border-amber-500 focus:ring-amber-500"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-slate-700">Telefono *</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="333 123 4567"
            required
            className="h-12 bg-white border-slate-200 focus:border-amber-500 focus:ring-amber-500"
          />
        </div>
      </div>

      {/* Città/Zona */}
      <div className="space-y-2">
        <Label htmlFor="city" className="text-slate-700">Città/Zona *</Label>
        <Input
          id="city"
          name="city"
          value={formData.city}
          onChange={handleChange}
          placeholder="Castelvetrano"
          required
          className="h-12 bg-white border-slate-200 focus:border-amber-500 focus:ring-amber-500"
        />
      </div>

      {/* Descrizione lavoro */}
      <div className="space-y-2">
        <Label htmlFor="message" className="text-slate-700">Descrizione lavoro</Label>
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Descrivi brevemente il lavoro che vorresti realizzare..."
          rows={4}
          className="bg-white border-slate-200 focus:border-amber-500 focus:ring-amber-500 resize-none"
        />
      </div>

      {/* GDPR Consent */}
      <div className="flex items-start space-x-3 p-4 bg-slate-50 rounded-xl">
        <Checkbox
          id="privacy"
          checked={privacyAccepted}
          onCheckedChange={setPrivacyAccepted}
          className="mt-0.5 data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
        />
        <div className="grid gap-1.5 leading-none">
          <label
            htmlFor="privacy"
            className="text-sm text-slate-600 cursor-pointer leading-relaxed"
          >
            Dichiaro di aver letto l'{' '}
            <Link to="/privacy-policy" className="text-amber-600 hover:underline font-medium" target="_blank">
              Informativa Privacy
            </Link>{' '}
            e acconsento al trattamento dei miei dati per gestire la mia richiesta di contatto o preventivo. *
          </label>
        </div>
      </div>

      <Button
        type="submit"
        disabled={isSubmitting || !privacyAccepted}
        className={`w-full h-12 text-base font-medium transition-all ${
          variant === 'dark'
            ? 'bg-white text-amber-700 hover:bg-amber-50 disabled:bg-white/50'
            : 'bg-amber-600 hover:bg-amber-700 text-white disabled:bg-amber-300'
        }`}
      >
        {isSubmitting ? (
          <span className="flex items-center gap-2">
            <span className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            Invio in corso...
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <Send className="w-5 h-5" />
            Invia Richiesta
          </span>
        )}
      </Button>
      <p className="text-xs text-slate-500 text-center">
        I campi contrassegnati con * sono obbligatori.
      </p>
    </form>
  );
};

export default ContactForm;
